import {
  BreadcrumbItem,
  generateBreadcrumbJsonLd,
} from '@shared/seo-config';

export const SEO_MANAGER_SCRIPT_ATTRIBUTE = 'data-seo-manager';

function normalizeJsonLd(obj: object): object {
  if ('@context' in obj) return obj;
  return { '@context': 'https://schema.org', ...obj };
}

/**
 * Remove only the JSON-LD nodes created by one SEO manager owner.
 *
 * A node may have been removed by another DOM owner (or by a browser
 * integration) before cleanup runs. Checking parentNode keeps cleanup
 * idempotent while retaining ownership by exact node reference.
 */
export function removeOwnedJsonLdScripts(ownedScripts: HTMLScriptElement[]): void {
  ownedScripts.forEach((script) => {
    if (script.parentNode) {
      script.parentNode.removeChild(script);
    }
  });
  ownedScripts.length = 0;
}

/**
 * Replace an owner's structured-data scripts without touching scripts owned
 * by other SeoManager/useSeo instances.
 */
export function replaceOwnedJsonLdScripts(
  breadcrumbs: BreadcrumbItem[] | undefined,
  customJsonLd: object | object[] | undefined,
  ownedScripts: HTMLScriptElement[],
  baseUrl: string,
): void {
  removeOwnedJsonLdScripts(ownedScripts);
  // The initial HTML already owns these two schemas. Replace them after the
  // public product API resolves, rather than leaving two Products in the DOM.
  if (customJsonLd && (Array.isArray(customJsonLd) ? customJsonLd : [customJsonLd])
    .some((schema) => (schema as any)['@type'] === 'Product')) {
    document.querySelectorAll('script[data-ssr-product-schema]').forEach((node) => node.remove());
  }

  const scriptsToAdd: object[] = [];

  if (breadcrumbs && breadcrumbs.length > 0) {
    scriptsToAdd.push(generateBreadcrumbJsonLd(breadcrumbs, baseUrl));
  }

  if (customJsonLd) {
    if (Array.isArray(customJsonLd)) {
      scriptsToAdd.push(...customJsonLd.map(normalizeJsonLd));
    } else {
      scriptsToAdd.push(normalizeJsonLd(customJsonLd));
    }
  }

  scriptsToAdd.forEach((data, index) => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute(SEO_MANAGER_SCRIPT_ATTRIBUTE, `schema-${index}`);
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);
    ownedScripts.push(script);
  });
}